import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { API } from "../../lib/api";

export default function Profile() {
  const { user, token, setToken, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const strongEnough = (pw) => pw.length >= 10 && /[A-Za-z]/.test(pw) && /\d/.test(pw);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("New passwords do not match.");
    if (!strongEnough(next)) return toast.error("Password must be 10+ chars with a letter and a number.");
    if (next === current) return toast.error("New password must differ from the current one.");
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.token && typeof setToken === "function") setToken(r.data.token);
      toast.success("Password updated. Other devices have been signed out.");
      setCurrent(""); setNext(""); setConfirm(""); setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-6 md:px-12 py-12 md:py-16 max-w-3xl">
      <div className="text-[11px] tracking-[0.32em] uppercase text-[#2A5BFF] mb-4">Profile</div>
      <h1 className="font-anton uppercase text-5xl md:text-6xl leading-[0.9]" data-testid="profile-heading">Your account</h1>

      <div className="mt-10 border border-white/10 divide-y divide-white/10" data-testid="profile-card">
        <Row label="First name" value={user.first_name} />
        <Row label="Email" value={user.email} />
        <Row label="Account ID" value={user.id} mono />
      </div>

      <div className="mt-8 border border-white/10" data-testid="change-password-card">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-white/[0.02]"
          data-testid="change-password-toggle"
        >
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">Security</div>
            <div className="text-white/90 mt-1">Change password</div>
          </div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#2A5BFF]">{open ? "Close" : "Open"}</div>
        </button>
        {open && (
          <form onSubmit={submit} className="px-5 pb-6 space-y-5" data-testid="change-password-form">
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">Current password</label>
              <input type="password" autoComplete="current-password" value={current}
                onChange={(e) => setCurrent(e.target.value)} required
                className="input-line focus-ring" data-testid="cp-current" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">New password</label>
              <input type="password" autoComplete="new-password" minLength={10} value={next}
                onChange={(e) => setNext(e.target.value)} required
                className="input-line focus-ring" data-testid="cp-new" />
              <p className="mt-2 text-[11px] text-white/40">Minimum 10 characters, must include a letter and a number.</p>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">Confirm new password</label>
              <input type="password" autoComplete="new-password" minLength={10} value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required
                className="input-line focus-ring" data-testid="cp-confirm" />
            </div>
            <button type="submit" disabled={busy}
              className="btn-primary px-6 py-3 text-[11px] tracking-[0.24em] uppercase disabled:opacity-60"
              data-testid="cp-submit">
              {busy ? "…" : "Update password"}
            </button>
          </form>
        )}
      </div>

      <button
        type="button"
        onClick={logout}
        className="btn-ghost mt-8 px-5 py-3 text-[11px] tracking-[0.22em] uppercase"
        data-testid="profile-logout"
      >
        Sign out
      </button>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-3 md:grid-cols-4 items-center px-5 py-4">
      <div className="col-span-1 text-[10px] tracking-[0.28em] uppercase text-white/40">{label}</div>
      <div className={`col-span-2 md:col-span-3 text-white/90 ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
    </div>
  );
}
